import { Alert, Stack, Typography as MuiTypography } from "@mui/material";
import { Banner, Typography as FoodsTypography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";

import { useGetTransportPost } from "src/api/client-api/post/useGetTransportPost";
import { ScoreCards } from "src/components/ScoreCards/ScoreCards";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { EntityScoresProvider } from "src/context/Scores/provider";
import { useTransportAppearance } from "./transportAppearance";

type TransportScoresPanelProps = {
    kind: "leg" | "circuit";
    transportId: number | undefined;
};

export const TransportScoresPanel = ({ kind, transportId }: TransportScoresPanelProps) => {
    const { t } = useTranslation();
    const appearance = useTransportAppearance("legacy");
    const { currentCampaign } = useFoodpilotContext();
    const { post, isLoading, isError } = useGetTransportPost({
        kind,
        transportId,
        campaignId: currentCampaign?.id,
        enabled: transportId !== undefined,
    });

    if (transportId === undefined) {
        return appearance === "product-v2" ?
                <Banner variant="info">
                    {t("transport.scores.save_before_scores", "Save this transport first to view its scores.")}
                </Banner>
            :   <Alert severity="info">
                    {t("transport.scores.save_before_scores", "Save this transport first to view its scores.")}
                </Alert>;
    }

    const title = t("transport.scores.title", "Transport scores");
    const help = t("transport.scores.one_tonne_help", "Scores are displayed for 1 tonne of transported product.");
    const unavailable = t("transport.scores.unavailable", "Transport scores are not available yet.");

    return (
        <Stack gap={2}>
            <Stack gap={0.5}>
                {appearance === "product-v2" ?
                    <>
                        <FoodsTypography component="h3" variant="big-semibold">
                            {title}
                        </FoodsTypography>
                        <FoodsTypography variant="body-regular">{help}</FoodsTypography>
                    </>
                :   <>
                        <MuiTypography variant="h3" sx={{ fontSize: (theme) => theme.spacing(2.25) }}>
                            {title}
                        </MuiTypography>
                        <MuiTypography variant="body2">{help}</MuiTypography>
                    </>
                }
            </Stack>
            {isError ?
                appearance === "product-v2" ?
                    <Banner variant="error">{unavailable}</Banner>
                :   <Alert severity="warning">{unavailable}</Alert>
            :   <EntityScoresProvider entity="transport">
                    <ScoreCards
                        appearance={appearance}
                        scores={post ? { olapScores: post.olapScores } : undefined}
                        isLoading={isLoading}
                    />
                </EntityScoresProvider>
            }
        </Stack>
    );
};
